import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { statusColor } from '../../config/sensorRanges';
import { alpha } from './helpers';

/* Styled pieces shared by more than one Dashboard card: the panel shell, the glance card,
   the rack-row bars that Plant health and Growth both end in, and the loading skeleton. */

/* ------------------------------------------------------------------ shells */

export const RADIUS = 16;

export const Panel = styled.section`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${RADIUS}px;
  padding: 18px 20px;
  min-width: 0;
  order: ${p => p.$order || 0};

  @media (max-width: 560px) { padding: 14px 14px; }
`;

export const PanelTitle = styled.h2`
  margin: 0 0 14px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.005em;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;

  .count { font-weight: 500; color: ${p => p.theme.colors.textMuted}; margin-left: 6px; }
  .aside { font-size: 12px; font-weight: 400; color: ${p => p.theme.colors.textMuted}; }

  a {
    /* The global 44px mobile touch target would push the panel body down; keep
       the tap area with padding pulled back by a negative margin instead. */
    min-height: 0;
    padding: 8px 0;
    margin: -8px 0;
    font-size: 12px;
    font-weight: 500;
    color: ${p => p.theme.colors.primary};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

export const Note = styled.p`
  margin: 0;
  color: ${p => p.theme.colors.textMuted};
  font-size: 13px;
`;

/* ------------------------------------------------------------------ at a glance */

export const GlanceCard = styled(Link)`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 16px;
  border-radius: ${RADIUS}px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &:hover { border-color: ${p => p.theme.colors.borderHover}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }

  /* One skeleton for all three glance cards: title + status, the big figure, three figures
     under a hairline, an optional sentence, then a foot pinned to the bottom (rack rows, or
     Weather's 24 h line). The figures rows sit at one height across the row of cards. */
  .k { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; }
  .word {
    display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
    font-size: 12px; font-weight: 500; padding: 2px 8px; border-radius: 10px;
    color: ${p => p.theme.colors.textSecondary};
    background: ${p => alpha(statusColor(p.theme, p.$status || 'normal'), 0.12)};
  }
  .word i { width: 6px; height: 6px; border-radius: 50%; background: ${p => statusColor(p.theme, p.$status || 'normal')}; }
  .big { margin-top: 8px; font-size: 28px; font-weight: 500; letter-spacing: -0.03em; line-height: 1.1; font-variant-numeric: tabular-nums; }
  .big small { font-size: 13px; font-weight: 400; letter-spacing: 0; color: ${p => p.theme.colors.textMuted}; margin-left: 8px; }
  .line { margin-top: 12px; font-size: 13px; color: ${p => p.theme.colors.textSecondary}; }
  .line b { font-weight: 500; color: ${p => p.theme.colors.text}; }
  .facts {
    margin-top: 12px; padding-top: 12px; border-top: 1px solid ${p => p.theme.colors.border};
    display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px;
  }
  .facts div { min-width: 0; }
  .facts b { display: block; font-size: 16px; font-weight: 500; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .facts span { display: block; margin-top: 2px; font-size: 12px; color: ${p => p.theme.colors.textMuted}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .foot { margin-top: auto; padding-top: 16px; }
`;

// One row per system: name, a healthy / stressed / unhealthy bar, healthy of total.
// The rows share one set of columns (subgrid), so a longer count on one row shortens every
// bar alike instead of only its own; browsers without subgrid size each row alone.
export const HealthRows = styled.ul`
  list-style: none; margin: 0; display: grid; gap: 8px 12px;
  grid-template-columns: 80px minmax(0, 1fr) auto;
  li {
    grid-column: 1 / -1; display: grid; align-items: center; gap: 12px; font-size: 13px; line-height: 16px;
    grid-template-columns: 80px minmax(0, 1fr) auto;
    grid-template-columns: subgrid;
  }
  li > span:first-child { color: ${p => p.theme.colors.textSecondary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  li > span:last-child { color: ${p => p.theme.colors.textMuted}; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .bar { display: flex; gap: 2px; height: 6px; border-radius: 3px; overflow: hidden; background: ${p => p.theme.colors.border}; }
  .bar i { display: block; min-width: 3px; }
`;

// The rows and their colour key, pinned to the card's foot. Plant health and Growth both end
// in five rows + one key line, so their rack rows line up however many lines of text sit above.
export const StageMix = styled.div`
  margin-top: auto;
  padding-top: 16px;
  .key { display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 12px; font-size: 12px; color: ${p => p.theme.colors.textSecondary}; }
  .key span { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
  .key i { width: 6px; height: 6px; border-radius: 50%; flex: 0 0 auto; }
  .key b { font-weight: 500; color: ${p => p.theme.colors.text}; font-variant-numeric: tabular-nums; }
  .key .due { color: ${p => p.theme.colors.textMuted}; }
`;

/* ------------------------------------------------------------------ skeleton */

export const Skel = styled.div`
  height: ${p => p.$h}px;
  border-radius: ${RADIUS}px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
`;
